<template>
    <div>
        <div class="step-box">
            <step
                titleColor="green"
                otherClass="first-step"
            >
                <p slot="title">Step 1</p>
                <p slot="text">Calculate Phytate Accessibility Factor</p>
            </step>
            <div class="column is-10 is-offset-1">
                <div class="card-content">
                    <p class=" has-text-centered">
                        The default Phytate Accessibility Factor is
                    </p>
                    <p class=" has-text-centered has-text-primary has-text-weight-bold is-size-4-desktop is-size-6-mobile">
                        {{defaultValue*100}} %
                    </p>
                    <p class=" has-text-centered">Your Accessible phytate P estimate (raw material leverage only) is</p>
                    <p class=" has-text-centered has-text-primary has-text-weight-bold is-size-4-desktop is-size-6-mobile">
                        {{roundedAccessiblePhytate}} %
                    </p>
                </div>
                <div
                    v-if="selectedAnimal != 'Layer' && selectedAnimal != 'Aqua' && (user.type == 'Super Admin' || user.type == 'Administrator' || user.type == 'Advanced')"
                    class="text-center flex-grow"
                >
                    <p class=" has-text-centered">
                        Do you wish to complete an assessment to calculate the corrected Phytate P value based on the non-raw material leverage?
                    </p>
                    <button
                        v-if="!showCmp"
                        class="btn-large btn-primary mx-2 my-4 inline-flex justify-center items-center w-full sm:w-1/2 md:w-1/3"
                        @click="startAssessment"
                    >
                        <span class="icon is-small mr-2">
                            <i class="fas fa-chevron-down"></i>
                        </span>
                        <span>Start assessment</span>
                    </button>
                    <button
                        v-else
                        class="btn-large btn-blank mx-2 my-4 inline-flex justify-center items-center w-full sm:w-1/2 md:w-1/3"
                        @click="clickedDefault"
                    >
                        <span class="icon is-small mr-2">
                            <i class="fas fa-times"></i> </span>
                        <span>Use default</span>
                    </button>
                    <p class="text-grey-dark text-xs sm:text-sm md:text-base"><span><i class="fas fa-check-circle mr-1 font-hairline text-green"></i></span><span v-text="`Using ${showCmp ? 'custom factor' : 'default factor'}`"></span></p>
                </div>
            </div>
        </div>
        <div id="accessibility-step2">
            <transition
                name="fade"
                v-on:enter="scrollToId('accessibility-step2')"
            >
                <div
                    class="step-box"
                    v-show="showCmp"
                >
                    <step titleColor="purple">
                        <p slot="title">Step 2</p>
                        <p slot="text">Phytate Accessibility Factor Calculator</p>
                        <a
                            slot="header-end"
                            @click="showHelpModal = true"
                            class="pr-8 inline-flex"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                class="icon-light fill-current h-6 w-6"
                            >
                                <path
                                    class="primary"
                                    d="M5 8a7 7 0 1 1 10.62 6l-.64 3.2a1 1 0 0 1-.98.8h-4a1 1 0 0 1-.98-.8L8.38 14A7 7 0 0 1 5 8zm12 0a5 5 0 0 0-5-5 1 1 0 0 0 0 2 3 3 0 0 1 3 3 1 1 0 0 0 2 0z"
                                />
                                <path
                                    class="secondary"
                                    d="M15 21a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2 1 1 0 0 1 0-2h6a1 1 0 0 1 0 2z"
                                />
                            </svg>
                            <span class="pl-1">How does this work?</span></a>
                    </step>
                    <accessibilitycmp
                        :totalPhytateP="phytate"
                        :accessiblePhytateP="accessiblePhytate"
                        :defaultValue="defaultValue"
                        @correctedPhytatePUpdated="payload => {correctedPhytateP = payload.correctedPhytateP}"
                        @accessibilityUpdated="payload => {accessibility = payload.correctedAccessibility}"
                        v-show="showCmp"
                    ></accessibilitycmp>
                </div>
            </transition>
        </div>
        <custom-modal
            :showModal="showHelpModal"
            title="How does this work?"
            @update:showModal="newValue => showHelpModal = newValue"
            :showFooter="true"
            :cardStyleClass="'w-full'"
            :modalStyleClass="'px-4 lg:px-32'"
            :cancelBtn="{ text: 'Close', type: 'blank' }"
            :showSubmitBtn="false"
        >
            <div
                slot="body"
                class="flex flex-col items-center xl:px-32"
            >
                <label
                    class="block text-grey-darker mb-4 text-lg sm:text-xl md:text-2xl"
                    for="matrixName"
                >
                    Factors that influence the solubility/accessibility of dietary phytate.
                </label>
                <div class="bg-blue-lightest border-l-4 border-blue-light text-blue-darkest px-4 py-3 mb-4 flex items-center">
                    <div class="h-8 w-8 mr-4 flex-no-shrink">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            class="icon-information w-full h-full stroke-current fill-none font-semibold text-primary inline-block"
                        >
                            <path
                                class="primary"
                                d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20z"
                            />
                            <path
                                class="secondary"
                                d="M11 12a1 1 0 0 1 0-2h2a1 1 0 0 1 .96 1.27L12.33 17H13a1 1 0 0 1 0 2h-2a1 1 0 0 1-.96-1.27L11.67 12H11zm2-4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"
                            /></svg>
                    </div>
                    <div>For more information refer to the <a
                            class="shadow-underline font-semibold"
                            href="/research-paper/pfn"
                            target="_blank"
                        >Phytate Free Nutrition paper</a>
                    </div>
                </div>
                <img
                    src="/images/accessibility.png"
                    alt="accessibility"
                >
            </div>
        </custom-modal>
    </div>
</template>
<script>
import accessibilitycmp from "../components/AccessibilityCmp";

export default {
    components: {
        accessibilitycmp
    },
    props: ["selectedAnimal", "defaultValue", "totalPhytateP", "user"],
    data() {
        return {
            showCmp: false,
            useDefault: true,
            accessibility: this.defaultValue,
            correctedPhytateP: 0.0,
            showHelpModal: false
        };
    },

    computed: {
        phytate() {
            return Number(this.totalPhytateP);
        },
        accessiblePhytate() {
            return Number(this.totalPhytateP) * this.defaultValue;
        },
        roundedAccessiblePhytate() {
            return _.round(this.accessiblePhytate, 3);
        }
    },

    methods: {
        clickedDefault: function() {
            this.useDefault = true;
            this.showCmp = false;
            this.accessibility = this.defaultValue;
            this.correctedPhytateP = this.accessiblePhytate;
        },
        startAssessment: function() {
            this.showCmp = true;
            this.useDefault = false;
        },
        scrollToId(elId) {
            this.$scrollTo(`#${elId}`, 1000, { easing: "ease-in-out" });
        }
    },
    watch: {
        correctedPhytateP: function() {
            EventHub.$emit("correctedPhytatePUpdated", this.correctedPhytateP);
        },
        accessiblePhytate: function() {
            EventHub.$emit("accessiblePhytatePUpdated", this.accessiblePhytate);
        },
        selectedAnimal: function(newValue, oldValue) {
            if (newValue == "Layer") {
                this.showCmp = false;
            }
        },
        accessibility: function() {
            EventHub.$emit("correctedAccessibilityUpdated", this.accessibility);
        }
    }
};
</script>
<style type="text/css">
.card-primary {
    background-color: #22a7f0;
    border-bottom-right-radius: 5px;
}
</style>
